import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Info, Sparkles, Wand2 } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import type { RatePeriod } from '../data/model'
import { todayISO } from '../lib/dates'
import { aiFailureText, askAi, type AiFailure } from '../lib/aiClient'
import { coldPriceRequest, occupancyPriceRequest, priceCountry, type PriceCountry } from '../lib/aiPriceRequest'
import type { PriceAdviceResult } from '../lib/priceAdvice'
import {
  BASE_ROW_ID, WEEKEND_ROW_ID,
  currentAveragePrice, findBaseRate, parsePlanJson,
  planToRatePeriods, validatePricePlan,
  type PlanIssue, type PricePlan,
} from '../lib/pricePlan'
import { Badge, Button, Card, Field, Input, Select, cx } from '../ui/kit'

/**
 * Die beiden KI-Preisflächen.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * WO SIE SITZEN — UND WARUM DORT.
 *
 * KALTSTART: in der Preisliste selbst, an der Stelle, an der „Keine Ergebnisse"
 * stünde. Ein neuer Vermieter sucht nicht nach einem KI-Menüpunkt; er öffnet die
 * Einstellungen, sieht eine leere Saisonliste und weiß nicht weiter. GENAU DA muss
 * das Angebot stehen. Eine Funktion, die er nicht findet, existiert nicht — in
 * diesem Projekt ist das schon zweimal passiert (die Preisempfehlungen standen
 * zuerst nur in den Einstellungen und wurden dort nicht gesehen; die
 * Kinderermäßigung stand bei den Stammdaten und wurde dort nicht gesucht).
 *
 * Sie verschwindet, sobald es Saisonpreise gibt (`hasSeasonalRates`). Ein
 * Kaltstart-Angebot bei einem eingerichteten Betrieb wäre Lärm — dort übernimmt die
 * zweite Fläche.
 *
 * AUS DER BELEGUNG: unter den gerechneten Preisempfehlungen, als deren Fortsetzung.
 * Die Zahlen daneben stammen aus `buildPriceAdvice`; die KI rechnet sie nicht nach,
 * sie ordnet sie ein und formuliert die Begründung. Deshalb steht sie nicht
 * daneben, sondern darunter — erst die Rechnung, dann das Urteil.
 *
 * WAS BEIDE GEMEINSAM HABEN: das Ergebnis ist eine Liste zum Ankreuzen, nicht ein
 * Absatz zum Lesen. Jede Zeile trägt ihre Begründung, ihren heutigen Preis aus dem
 * eigenen Betrieb und einen änderbaren Vorschlagspreis; abgewählt wird mit einem
 * Klick. Erst der Knopf am Ende schreibt in die Preisliste.
 * ═══════════════════════════════════════════════════════════════════════════════
 */

/** Wie viele Ausgabe-Tokens ein Plan braucht. Sechs Zeilen mit Begründung sind lang. */
const PLAN_MAX_TOKENS = 1600

/* ─────────────────────────────────────────────────────────── Der Zustand */

type PlanPhase =
  | { phase: 'idle' }
  | { phase: 'asking' }
  | { phase: 'failed'; failure: AiFailure }
  /** Antwort da, aber nach der Prüfung blieb nichts Verwertbares übrig. */
  | { phase: 'unusable'; issues: PlanIssue[] }
  | { phase: 'ready'; plan: PricePlan; issues: PlanIssue[]; notice: string | null }
  | { phase: 'applied'; count: number }

/* ───────────────────────────────────────────────────────── Fläche 1: Kaltstart */

const CHARACTERS = {
  de: ['Sommerdestination', 'Winter- und Skigebiet', 'ganzjährig gleichmäßig',
    'Stadt und Städtereisen', 'See und Baden', 'Wandern und Berge'],
  en: ['Summer destination', 'Winter and ski area', 'Even all year round',
    'City breaks', 'Lake and swimming', 'Hiking and mountains'],
}

const AUDIENCES = {
  de: ['Familien mit Kindern', 'Paare und Kurzurlauber', 'Wanderer und Radfahrer',
    'Wintersportler', 'Geschäftsreisende und Monteure', 'gemischt'],
  en: ['Families with children', 'Couples and short breaks', 'Hikers and cyclists',
    'Winter sports guests', 'Business travellers and contractors', 'Mixed'],
}

/**
 * Der Kaltstart: aus Auswahlwerten und Zahlen ein vollständiger Vorschlag.
 *
 * Geschlossene Auswahl und Zahlen statt Freitext. Wer vor einer leeren Preisliste steht, hat keine
 * Geduld für ein Formular — und alles, was aus den Einheiten ohnehin bekannt ist
 * (Art, Größe, Betten), wird nicht gefragt, sondern mitgeschickt.
 */
export function ColdStartPriceCard() {
  const { lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const today = todayISO()

  /*
   * Die beiden Auswahlfelder halten eine POSITION und keinen Text. Sonst stünde nach
   * einem Sprachwechsel „Sommerdestination" in einer Liste, die nur noch englische
   * Einträge hat — das Feld wirkt dann leer, obwohl etwas ausgewählt ist.
   */
  const [country, setCountry] = useState<PriceCountry>(() => priceCountry(store.db.tenant.country))
  const [character, setCharacter] = useState(0)
  const [audience, setAudience] = useState(0)
  const [state, setState] = useState<PlanPhase>({ phase: 'idle' })

  const characters = CHARACTERS[de ? 'de' : 'en']
  const audiences = AUDIENCES[de ? 'de' : 'en']
  const activeUnits = store.db.units.filter((u) => u.active)
  const baseRate = findBaseRate(store.db.rate_periods, today)

  const ask = async () => {
    setState({ phase: 'asking' })
    const result = await askAi({
      purpose: 'price_advice',
      request: coldPriceRequest({
        country, character, audience, units: activeUnits,
        today, de, currency: store.db.tenant.currency,
        currentBasePrice: baseRate?.price_per_night ?? null,
      }),
      maxTokens: PLAN_MAX_TOKENS,
      live: store.live,
      de,
    })
    setState(toPhase(result, today, baseRate?.price_per_night ?? null, de))
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium flex items-center gap-2">
        <Wand2 size={18} />
        {de ? 'Preisliste aus dem Nichts' : 'A price list from scratch'}
      </h2>

      <p className="text-sm text-muted">
        {de
          ? 'Sie haben noch keine Saisonpreise. Wählen Sie Land, Saison und Gästegruppe, und die KI schlägt Ihnen einen Grundpreis, Saisonzeiträume mit Zu- und Abschlägen, einen Wochenendaufschlag und Mindestaufenthalte vor — als Anhaltspunkt, den Sie Zeile für Zeile übernehmen oder verwerfen.'
          : 'You have no seasonal rates yet. Select country, season and guest group and the AI will propose a base price, seasonal periods with premiums and discounts, a weekend surcharge and minimum stays — a starting point you can accept or discard row by row.'}
      </p>

      {activeUnits.length === 0 ? (
        <p className="text-sm text-muted">
          {de
            ? 'Legen Sie zuerst eine Einheit an — ohne sie gibt es nichts zu bepreisen.'
            : 'Add a unit first — without one there is nothing to price.'}
        </p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={de ? 'Land' : 'Country'}>
              <Select value={country} onChange={e => setCountry(priceCountry(e.target.value))}>
                {(['AT', 'DE', 'CH', 'GB', 'US', 'OTHER'] as const).map(c =>
                  <option key={c} value={c}>{c === 'OTHER' ? (de ? 'Anderes Land' : 'Other country') : c}</option>)}
              </Select>
            </Field>
            <Field label={de ? 'Wann ist bei Ihnen etwas los?' : 'When is your season?'}>
              <Select
                value={character}
                onChange={(e) => setCharacter(Number(e.target.value))}
              >
                {characters.map((c, i) => <option key={c} value={i}>{c}</option>)}
              </Select>
            </Field>
            <Field label={de ? 'Wer wohnt bei Ihnen?' : 'Who stays with you?'}>
              <Select
                value={audience}
                onChange={(e) => setAudience(Number(e.target.value))}
              >
                {audiences.map((a, i) => <option key={a} value={i}>{a}</option>)}
              </Select>
            </Field>

          </div>

          {/* Was die KI ohnehin schon weiß — damit niemand es ein zweites Mal eintippt
              und niemand rätselt, was da eigentlich mitgeschickt wird. */}
          <p className="text-xs text-muted">
            {de ? 'Übertragen werden die Auswahlwerte und Zahlen zu Art, Größe, Schlafzimmern und Kapazität der Einheiten, keine Namen oder Ausstattung: ' : 'Only selections and figures for unit type, size, bedrooms and capacity are sent, without names or amenities: '}
            {activeUnits.length} {de ? 'Einheiten' : 'units'}
            {' · '}
            {[...new Set(activeUnits.map((u) => u.kind))].join(', ')}
            {' · '}
            {de ? 'bis ' : 'up to '}
            {Math.max(...activeUnits.map((u) => u.max_occupancy))}
            {de ? ' Personen' : ' guests'}
            {baseRate && ` · ${de ? 'heutiger Preis ' : 'price today '}${baseRate.price_per_night}`}
          </p>

          <AskButton
            state={state}
            de={de}
            label={de ? 'Preisvorschlag erstellen' : 'Create a price proposal'}
            onAsk={() => void ask()}
          />
        </>
      )}

      <PlanOutcome
        state={state}
        de={de}
        onReset={() => setState({ phase: 'idle' })}
        onApplied={(count) => setState({ phase: 'applied', count })}
        groundTruth={de
          ? 'Diesem Vorschlag liegen keine Marktdaten zugrunde, sondern die gewählte Saison, das Land und die Merkmale Ihrer Einheiten. Er ist ein Anhaltspunkt, kein Marktpreis — vergleichen Sie ihn mit dem, was Sie über Ihren Ort wissen.'
          : 'This proposal is not based on market data but on the selected season, country and unit attributes. It is a starting point, not a market price — compare it with what you know about your area.'}
      />
    </Card>
  )
}

/* ────────────────────────────────────── Fläche 2: begründet aus der Belegung */

/**
 * Der begründete Vorschlag aus der eigenen Belegung.
 *
 * Steht als Block UNTER den gerechneten Empfehlungen, weil er sie fortsetzt: die
 * Karte darüber sagt, WAS auffällt, dieser Block sagt, was daraus folgt — welche
 * Woche gefragt ist, wo noch Luft ist, und mit welchem Preis man darauf antwortet.
 *
 * Er erscheint nur, wenn die Rechnung überhaupt etwas gefunden hat. Eine KI zu
 * fragen, während die eigenen Zahlen nichts hergeben, kostet Geld und liefert
 * Allgemeinplätze.
 */
export function BookingsPricePlanBlock({ result }: { result: PriceAdviceResult }) {
  const { lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const today = todayISO()
  const [state, setState] = useState<PlanPhase>({ phase: 'idle' })

  const baseRate = findBaseRate(store.db.rate_periods, today)
  const enough = result.spans.length > 0 || result.weekend != null

  const ask = async () => {
    setState({ phase: 'asking' })
    const aiResult = await askAi({
      purpose: 'price_advice',
      request: occupancyPriceRequest({ today, de, currency: store.db.tenant.currency, result }),
      maxTokens: PLAN_MAX_TOKENS,
      live: store.live,
      de,
    })
    setState(toPhase(
      aiResult, today, baseRate?.price_per_night ?? Math.round(result.averagePrice), de))
  }

  if (!enough) return null

  return (
    <div className="border-t border-border pt-3 space-y-2">
      <h3 className="text-sm font-medium flex items-center gap-2">
        <Sparkles size={15} />
        {de ? 'Vorschlag begründen lassen' : 'Ask for a reasoned proposal'}
      </h3>
      <p className="text-xs text-muted">
        {de
          ? 'Die KI bekommt genau die Zahlen von oben — gerundete Auslastung, heutige Preise und Ferien-ja/nein — und macht daraus benannte Saisonzeiträume mit Preis und Begründung. Gerechnet wird weiterhin hier, nicht dort.'
          : 'The AI receives exactly the figures above — rounded occupancy, current prices and whether school holidays overlap — and turns them into named seasonal periods with a price and a reason. The arithmetic stays here, not there.'}
      </p>

      <AskButton
        state={state}
        de={de}
        label={de ? 'Zeiträume und Preise vorschlagen' : 'Propose periods and prices'}
        onAsk={() => void ask()}
      />

      <PlanOutcome
        state={state}
        de={de}
        onReset={() => setState({ phase: 'idle' })}
        onApplied={(count) => setState({ phase: 'applied', count })}
        groundTruth={de
          ? 'Grundlage sind Ihre eigenen Buchungen und Preise — keine Marktdaten und keine Preise anderer Häuser. Prüfen Sie die Vorschläge gegen das, was Sie über Ihren Ort wissen.'
          : 'This rests on your own bookings and prices — no market data and no other properties’ prices. Check the suggestions against what you know about your area.'}
      />
    </div>
  )
}

/* ───────────────────────────────────────────────── Vom Ergebnis zum Zustand */

/**
 * Die Antwort der KI, geprüft — in einen Zustand, den die Oberfläche zeigen kann.
 *
 * Hier landet auch der Demomodus: das hinterlegte Beispiel ist ein Absatz Fließtext
 * und kein Plan, es fällt also durch `parsePlanJson`. Das ist kein Fehler des
 * Demomodus, sondern seine Bauart — deshalb liegt weiter unten ein vorbereiteter
 * Beispielplan, der mit demselben Hinweis beschriftet wird.
 */
function toPhase(
  result: Awaited<ReturnType<typeof askAi>>, today: string, referencePrice: number | null,
  de: boolean,
): PlanPhase {
  if (!result.ok) return { phase: 'failed', failure: result.failure }

  const parsed = parsePlanJson(result.text)
  const check = validatePricePlan(parsed, { today, referencePrice })

  if (check.plan && check.plan.periods.length > 0) {
    return { phase: 'ready', plan: check.plan, issues: check.issues, notice: result.notice }
  }

  if (result.demo) {
    // Die Demo darf nicht leer ausgehen: der Interessent sieht sonst eine Funktion,
    // die nichts kann. Das Beispiel ist beschriftet und gibt sich nicht als frisch
    // gerechnet aus — `notice` steht sichtbar darüber.
    //
    // Es läuft durch DIESELBE Prüfung wie eine echte Antwort. Nicht aus Misstrauen
    // gegen den eigenen Text, sondern weil ein Beispiel, das an der Prüfung
    // vorbeigeht, irgendwann etwas zeigt, das die Prüfung nie durchgelassen hätte —
    // ein Zeitraum aus dem Vorjahr etwa, sobald der Kalender weiterläuft.
    const demo = validatePricePlan(demoPlanAnswer(today, de), { today, referencePrice })
    if (demo.plan && demo.plan.periods.length > 0) {
      return { phase: 'ready', plan: demo.plan, issues: [], notice: result.notice }
    }
  }

  return { phase: 'unusable', issues: check.issues.length > 0 ? check.issues : [{ kind: 'not_json', label: '' }] }
}

/**
 * Das nächste Vorkommen eines Zeitraums, der jedes Jahr wiederkehrt.
 *
 * Ein fest verdrahtetes Jahr wäre in zwölf Monaten ein Zeitraum aus der
 * Vergangenheit — und die Demo zeigte dem Interessenten ausgerechnet den Fehler,
 * den die Prüfung verhindern soll. Gesucht wird deshalb das erste Jahr, in dem der
 * Zeitraum noch vollständig vor uns liegt; ein Zeitraum über den Jahreswechsel
 * (`to` vor `from`) endet im Folgejahr.
 */
function upcoming(today: string, from: string, to: string): { from: string; to: string } {
  const year = Number(today.slice(0, 4))
  for (let i = 0; i < 3; i += 1) {
    const start = `${year + i}-${from}`
    const end = `${year + i + (to < from ? 1 : 0)}-${to}`
    if (start >= today) return { from: start, to: end }
  }
  return { from: `${year + 1}-${from}`, to: `${year + 1 + (to < from ? 1 : 0)}-${to}` }
}

/**
 * Der Beispielplan für die öffentliche Demo, in der Form einer echten Antwort.
 *
 * Er liegt hier und nicht in `aiBudget.ts`, weil er die Form DIESER Fläche hat: das
 * dort hinterlegte Beispiel ist ein Absatz und beschreibt dieselbe Sache in der
 * Sprache eines Fließtextes. Er wird bewusst als ROHE Antwort gebaut und nicht als
 * fertiger Plan — so geht er durch dieselbe Prüfung wie alles andere.
 */
function demoPlanAnswer(today: string, de: boolean): Record<string, unknown> {
  const christmas = upcoming(today, '12-20', '01-06')
  const winter = upcoming(today, '02-07', '02-28')
  const summer = upcoming(today, '07-04', '08-31')
  return de
    ? {
        basePrice: 95,
        baseReason: 'Ausgangspunkt für die ruhigen Wochen im Frühjahr und Spätherbst.',
        baseMinNights: 2,
        periods: [
          {
            name: 'Weihnachten und Neujahr', ...christmas,
            pricePerNight: 165, minNights: 5,
            reason: 'Die Feiertage sind in fast jeder Region die gefragteste Zeit des '
              + 'Winters; die Gäste bleiben länger und buchen früher.',
          },
          {
            name: 'Semesterferien', ...winter,
            pricePerNight: 140, minNights: 4,
            reason: 'Familien mit schulpflichtigen Kindern sind an diese Wochen gebunden '
              + 'und weichen kaum aus.',
          },
          {
            name: 'Hochsaison Sommer', ...summer,
            pricePerNight: 135, minNights: 5,
            reason: 'Die Sommerferien überschneiden sich in fast allen deutschsprachigen '
              + 'Bundesländern, die Nachfrage ist über Wochen gleichmäßig hoch.',
          },
        ],
        weekendPct: 12,
        weekendReason: 'Freitag- und Samstagnächte sind bei Kurzurlaubern gefragter als '
          + 'die Wochenmitte.',
        basis: 'Allgemeines Wissen über die Region, die Jahreszeiten und die Art des '
          + 'Objekts — ein Anhaltspunkt, kein Marktpreis.',
      }
    : {
        basePrice: 95,
        baseReason: 'A starting point for the quiet weeks in spring and late autumn.',
        baseMinNights: 2,
        periods: [
          {
            name: 'Christmas and New Year', ...christmas,
            pricePerNight: 165, minNights: 5,
            reason: 'In almost every region the holidays are the most sought-after part '
              + 'of the winter; guests stay longer and book earlier.',
          },
          {
            name: 'Half-term holidays', ...winter,
            pricePerNight: 140, minNights: 4,
            reason: 'Families with school-age children are tied to these weeks and have '
              + 'little room to move.',
          },
          {
            name: 'Summer high season', ...summer,
            pricePerNight: 135, minNights: 5,
            reason: 'The summer holidays overlap across most regions, so demand stays '
              + 'high for weeks on end.',
          },
        ],
        weekendPct: 12,
        weekendReason: 'Friday and Saturday nights are more sought after by short-break '
          + 'guests than midweek nights.',
        basis: 'General knowledge about the region, the seasons and this type of '
          + 'property — a starting point, not a market price.',
      }
}

/* ─────────────────────────────────────────────────────────────── Bausteine */

function AskButton({
  state, de, label, onAsk,
}: { state: PlanPhase; de: boolean; label: string; onAsk: () => void }) {
  if (state.phase === 'ready' || state.phase === 'applied') return null
  return (
    <Button
      variant="primary"
      size="sm"
      disabled={state.phase === 'asking'}
      onClick={onAsk}
    >
      <Sparkles size={15} />
      {state.phase === 'asking' ? (de ? 'Einen Moment …' : 'One moment …') : label}
    </Button>
  )
}

/**
 * Was nach dem Fragen zu sehen ist: Fehler, verworfener Plan, Plan zum Ankreuzen
 * oder die Bestätigung.
 *
 * Ein Bauteil für alle vier Ausgänge, weil beide Flächen dieselben vier haben — und
 * zwei Fassungen desselben Fehlertextes wären zwei Fassungen desselben Fehlers.
 */
function PlanOutcome({
  state, de, groundTruth, onReset, onApplied,
}: {
  state: PlanPhase
  de: boolean
  /** Der Satz, worauf der Vorschlag beruht. Steht immer beim Ergebnis. */
  groundTruth: string
  onReset: () => void
  onApplied: (count: number) => void
}) {
  const navigate = useNavigate()

  if (state.phase === 'failed') {
    const text = aiFailureText(state.failure, de)
    return (
      <div className="rounded-xl bg-surface-2 p-3 space-y-2">
        <p className="text-sm font-medium">{text.title}</p>
        <p className="text-sm text-muted">{text.body}</p>
        <div className="flex gap-2">
          {text.action && (
            <Button size="sm" variant="primary" onClick={() => navigate(text.action!.to)}>
              {text.action.label}
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={onReset}>
            {de ? 'Noch einmal versuchen' : 'Try again'}
          </Button>
        </div>
      </div>
    )
  }

  if (state.phase === 'unusable') {
    return (
      <div className="rounded-xl bg-surface-2 p-3 space-y-2">
        <p className="text-sm font-medium">
          {de ? 'Der Vorschlag war nicht plausibel' : 'The proposal was not plausible'}
        </p>
        <p className="text-sm text-muted">
          {de
            ? 'Wir prüfen jeden Vorschlag nach, bevor er hier erscheint — dieser hat die Prüfung nicht bestanden und wurde verworfen. Bitte versuchen Sie es noch einmal.'
            : 'Every proposal is checked before it appears here — this one did not pass and was discarded. Please try again.'}
        </p>
        <IssueList issues={state.issues} de={de} />
        <Button size="sm" variant="secondary" onClick={onReset}>
          {de ? 'Noch einmal versuchen' : 'Try again'}
        </Button>
      </div>
    )
  }

  if (state.phase === 'applied') {
    return (
      <div className="rounded-xl bg-ok-soft p-3 space-y-2">
        <p className="text-sm font-medium flex items-center gap-2 text-ok">
          <CheckCircle2 size={16} />
          {de
            ? `${state.count} ${state.count === 1 ? 'Zeile' : 'Zeilen'} übernommen`
            : `${state.count} ${state.count === 1 ? 'row' : 'rows'} added`}
        </p>
        <p className="text-sm">
          {de
            ? 'Sie stehen jetzt oben in der Liste der Saisonpreise und lassen sich dort jederzeit ändern oder löschen. Bestehende Buchungen ändern sich dadurch NICHT — sie sind bereits bepreist.'
            : 'They are now in the list of seasons above and can be changed or deleted there at any time. Existing bookings are NOT changed — they are already priced.'}
        </p>
        <Button size="sm" variant="secondary" onClick={onReset}>
          {de ? 'Fertig' : 'Done'}
        </Button>
      </div>
    )
  }

  if (state.phase !== 'ready') return null

  return (
    <PlanReview
      plan={state.plan}
      issues={state.issues}
      notice={state.notice}
      groundTruth={groundTruth}
      de={de}
      onDiscard={onReset}
      onApplied={onApplied}
    />
  )
}

/* ────────────────────────────────────────────── Die Tabelle zum Ankreuzen */

interface Row {
  id: string
  name: string
  from: string
  lastDay: string
  price: number
  minNights: number | null
  reason: string
  /** Nur der Wochenendzuschlag gilt an ausgewählten Tagen. */
  weekendOnly?: boolean
}

/**
 * Der Vorschlag als Liste, die man Zeile für Zeile annimmt.
 *
 * DREI DINGE STEHEN IN JEDER ZEILE, und keines davon ist verzichtbar: der
 * vorgeschlagene Preis (änderbar), der Preis, den der Betrieb für diesen Zeitraum
 * HEUTE verlangt (aus `resolveNightlyRate`, also aus der echten Preisrechnung), und
 * der Satz, warum. Ohne den heutigen Preis ist der Vorschlag nicht einzuordnen; ohne
 * die Begründung wird er blind übernommen oder blind ignoriert.
 */
function PlanReview({
  plan, issues, notice, groundTruth, de, onDiscard, onApplied,
}: {
  plan: PricePlan
  issues: PlanIssue[]
  notice: string | null
  groundTruth: string
  de: boolean
  onDiscard: () => void
  onApplied: (count: number) => void
}) {
  const { formatMoney, formatDate } = useI18n()
  const store = useStore()
  const today = todayISO()

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [{
      id: BASE_ROW_ID,
      name: de ? 'Grundpreis' : 'Base price',
      from: today,
      lastDay: '',
      price: plan.basePrice,
      minNights: plan.baseMinNights,
      reason: plan.baseReason,
    }]
    for (const p of plan.periods) {
      out.push({
        id: p.id, name: p.name, from: p.from, lastDay: p.lastDay,
        price: p.pricePerNight, minNights: p.minNights, reason: p.reason,
      })
    }
    if (plan.weekendPct != null) {
      out.push({
        id: WEEKEND_ROW_ID,
        name: de ? 'Wochenendzuschlag' : 'Weekend surcharge',
        from: today,
        lastDay: '',
        price: Math.round(plan.basePrice * (100 + plan.weekendPct) / 100),
        minNights: null,
        reason: plan.weekendReason,
        weekendOnly: true,
      })
    }
    return out
  }, [plan, de, today])

  const [selected, setSelected] = useState<Set<string>>(() => new Set(rows.map((r) => r.id)))
  const [prices, setPrices] = useState<Map<string, number>>(() => new Map())
  const [saving, setSaving] = useState(false)

  const priceOf = (r: Row) => prices.get(r.id) ?? r.price

  /**
   * Der heutige Preis desselben Zeitraums — die echte Zahl aus dem Betrieb.
   * Für den Grundpreis und den Zuschlag gibt es keinen Zeitraum, dort steht die
   * hinterlegte Ganzjahreszeile.
   */
  const baseRate = findBaseRate(store.db.rate_periods, today)
  const todayPrice = (r: Row): number => {
    if (!r.lastDay) return baseRate?.price_per_night ?? 0
    return currentAveragePrice(store.db.units, store.db.rate_periods, r.from, r.lastDay)
  }

  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  const apply = async () => {
    setSaving(true)
    const created: RatePeriod[] = planToRatePeriods({
      plan,
      selected,
      priceOverrides: prices,
      tenantId: store.db.tenant.id,
      today,
      existing: store.db.rate_periods,
      newId: () => crypto.randomUUID(),
      labels: {
        base: de ? 'Grundpreis' : 'Base price',
        weekend: de ? 'Wochenendzuschlag' : 'Weekend surcharge',
      },
    })
    // Nacheinander und nicht parallel: `upsertRatePeriod` rechnet mit dem Stand, den
    // es vorfindet, und schreibt ihn zurück. Parallel gestartet überschreiben sich
    // die Zustandsaktualisierungen gegenseitig.
    for (const row of created) await store.upsertRatePeriod(row)
    setSaving(false)
    onApplied(created.length)
  }

  return (
    <div className="space-y-3 rounded-xl bg-surface-2 p-3">
      {notice && (
        <Badge tone="info" className="whitespace-normal text-left">
          <Info size={12} className="shrink-0" />
          {notice}
        </Badge>
      )}

      <ul className="divide-y divide-border">
        {rows.map((r) => {
          const on = selected.has(r.id)
          const now = todayPrice(r)
          const delta = now > 0 ? Math.round((priceOf(r) / now - 1) * 100) : null
          return (
            <li key={r.id} className={cx('py-2.5 space-y-1', !on && 'opacity-50')}>
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="w-4 h-4 mt-1 accent-[var(--color-accent)] shrink-0"
                  checked={on}
                  onChange={() => toggle(r.id)}
                  aria-label={r.name}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  <div className="text-xs text-muted">
                    {r.lastDay
                      ? `${formatDate(r.from)} – ${formatDate(r.lastDay)}`
                      : (de ? 'gilt durchgehend' : 'applies throughout')}
                    {r.weekendOnly && ` · ${de ? 'nur Fr und Sa' : 'Fri and Sat only'}`}
                    {r.minNights != null
                      && ` · ${de ? `ab ${r.minNights} Nächten` : `min ${r.minNights} nights`}`}
                  </div>
                </div>
                <div className="w-24 shrink-0">
                  <Input
                    type="number"
                    step="1"
                    min={0}
                    className="text-right tabular-nums py-1.5 px-2"
                    value={priceOf(r)}
                    disabled={!on}
                    onChange={(e) => {
                      const next = new Map(prices)
                      next.set(r.id, Number(e.target.value))
                      setPrices(next)
                    }}
                    aria-label={`${r.name} — ${de ? 'Preis pro Nacht' : 'price per night'}`}
                  />
                </div>
              </div>

              <div className="pl-7 text-xs text-muted flex flex-wrap gap-x-3">
                {now > 0 && (
                  <span className="tabular-nums">
                    {de ? 'heute ' : 'today '}{formatMoney(Math.round(now))}
                    {delta != null && delta !== 0 && (
                      <span className={delta > 0 ? 'text-ok' : 'text-warn'}>
                        {' '}({delta > 0 ? '+' : ''}{delta} %)
                      </span>
                    )}
                  </span>
                )}
              </div>

              <p className="pl-7 text-sm">{r.reason}</p>
            </li>
          )
        })}
      </ul>

      {plan.basis && <p className="text-xs text-muted">{plan.basis}</p>}

      <IssueList issues={issues} de={de} />

      <div className="flex gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={saving || selected.size === 0}
          onClick={() => void apply()}
        >
          {de
            ? `${selected.size} ${selected.size === 1 ? 'Zeile' : 'Zeilen'} übernehmen`
            : `Apply ${selected.size} ${selected.size === 1 ? 'row' : 'rows'}`}
        </Button>
        <Button variant="secondary" size="sm" disabled={saving} onClick={onDiscard}>
          {de ? 'Verwerfen' : 'Discard'}
        </Button>
      </div>

      <p className="text-xs text-muted border-t border-border pt-2">{groundTruth}</p>
    </div>
  )
}

/* ─────────────────────────────────────────────── Was verworfen wurde, sichtbar */

/**
 * Die Prüfung, sichtbar gemacht.
 *
 * WARUM DAS ÜBERHAUPT AUF DEN SCHIRM KOMMT: der Vermieter soll sehen, dass zwischen
 * dem Modell und seiner Preisliste eine Prüfung steht. Verschwiegenes Wegwerfen sähe
 * aus wie eine KI, die mal drei und mal fünf Zeilen liefert — hier steht, dass es
 * fünf waren und warum zwei nicht durchkamen.
 *
 * Kein Fehlercode, keine Kennung: jede Ursache wird zu einem Satz, den ein Vermieter
 * ohne Rückfrage versteht.
 */
function IssueList({ issues, de }: { issues: PlanIssue[]; de: boolean }) {
  if (issues.length === 0) return null

  const text = (i: PlanIssue): string => {
    switch (i.kind) {
      case 'not_json':
        return de ? 'Die Antwort war nicht auswertbar.' : 'The answer could not be read.'
      case 'no_base_price':
        return de ? 'Kein brauchbarer Grundpreis.' : 'No usable base price.'
      case 'bad_dates':
        return de ? 'unklarer Zeitraum' : 'unclear period'
      case 'past':
        return de ? 'liegt in der Vergangenheit' : 'lies in the past'
      case 'bad_length':
        return de ? 'als Saison zu kurz oder zu lang' : 'too short or too long for a season'
      case 'too_far':
        return de ? 'liegt zu weit in der Zukunft' : 'too far in the future'
      case 'price_out_of_range':
        return de ? 'unplausibler Preis' : 'implausible price'
      case 'overlap':
        return de ? 'überschneidet einen anderen Zeitraum' : 'overlaps another period'
      case 'too_many':
        return de ? 'mehr Saisonstufen als sinnvoll' : 'more seasons than useful'
      case 'no_reason':
        return de ? 'ohne Begründung' : 'without a reason'
      case 'clamped_to_today':
        return de ? 'begann in der Vergangenheit, beginnt jetzt heute' : 'started in the past, now starts today'
      case 'min_nights_dropped':
        return de ? 'unplausibler Mindestaufenthalt, Angabe entfernt' : 'implausible minimum stay, dropped'
      case 'weekend_dropped':
        return de ? 'Wochenendaufschlag unplausibel, entfällt' : 'weekend surcharge implausible, dropped'
    }
  }

  return (
    <div className="text-xs text-muted space-y-0.5">
      <p>
        {de
          ? `Geprüft und angepasst: ${issues.length} ${issues.length === 1 ? 'Punkt' : 'Punkte'}.`
          : `Checked and adjusted: ${issues.length} ${issues.length === 1 ? 'point' : 'points'}.`}
      </p>
      <ul className="list-disc pl-4">
        {issues.map((i, n) => (
          <li key={`${i.kind}-${n}`}>
            {i.label ? `${i.label}: ` : ''}{text(i)}
          </li>
        ))}
      </ul>
    </div>
  )
}
